<script setup>
import ConfirmDialog from "@/components/dialogs/ConfirmDialog.vue"
import questionService from "@/services/question.service"
import Sortable from 'sortablejs'
import { useRoute, useRouter } from "vue-router"
import { VDataTable, VDataTableServer } from "vuetify/labs/VDataTable"

const router = useRouter()
const route = useRoute()
const searchQuery = ref(route.query.search || "")
const isConfirmDialogVisible = ref(false)
const selectedFormType = ref(route.query.formType || 1)

definePage({
  meta: {
    name: "questionnaire-question",
    subject: 'feedback.menu',
    action: 'questionnaire_question',
  },
})

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()
const snackbar = useToast()

const errors = ref({
  name: [],
  code: [],
  status: [],
})

const onError = error => {
  if (error.data) {
    errors.value = error.data.errors
  }
}

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order.toUpperCase()
}

const selectedQuestionGroup = ref(route.query.questionGroup || null)

// Headers
const headers = [
  { title: '', value: 'drag', sortable: false },
  { title: "", key: "data-table-expand" },
  {
    title: "Label",
    key: "label",
  },
  {
    title: "Order",
    key: "order",
  },
  {
    title: "Question Type",
    key: "question_type",
  },
  {
    title: "Question Key",
    key: "question_key",
  },
  {
    title: "Question Group",
    key: "question_group.name",
    sortable: false,
  },
  {
    title: "Department",
    key: "department.name",
    sortable: false,
  },
  {
    title: "Status",
    key: "status",
  },
  {
    title: "Actions",
    key: "actions",
    sortable: false,
  },
]

const subQuestionsHeader = [
  { title: '', value: 'drag', sortable: false },
  {
    title: "Label",
    key: "label",
  },
  {
    title: "Order",
    key: "order",
  },
  {
    title: "Question Type",
    key: "question_type",
  },
  {
    title: "Question Key",
    key: "question_key",
  },

  // {
  //   title: 'Question Group',
  //   key: 'question_group.name',
  //   sortable: false,
  // },
  {
    title: "Status",
    key: "status",
  },
  {
    title: "Actions",
    key: "actions",
    sortable: false,
  },
]

const { data: formTypes } = await useApi(
  createUrl("masters/form-types", {
    query: {
      select: ["id", "name"],

      // search: {
      //   unit_id: cookieRef('selectedUnit'),
      // },
    },
  }),
)

const { isFetching: isFetchingQuestionGroup, data: questionGroups } =
  useListFetchingService(
    createUrl("/masters/question-groups", {
      query: {
        form_type_id: selectedFormType,
      },
    }),
  )

 
const selectedDepartment = ref()

const { isFetching: isFetchingQuestions, data: questionsData, execute: fetchQuestions } = useApi(
  createUrl("/masters/questions", {
    query: {
      paged: 1,
      showAll: true,
      search: {
        label: searchQuery,
        question_group_id: selectedQuestionGroup,
        parent_id: 'null',
        department_id: selectedDepartment,
      },
      with: ["department"],
      itemsPerPage,
      page,
      sort: sortBy,
      order_by: orderBy,
      form_type_id: selectedFormType,
    },
  }),
)


const { isFetching: isFetchingDepartment, data: departments } = await useApi(createUrl('/masters/departments'))

const questions = computed(
  () => questionsData.value?.data ?? questionsData.value,
)

const totalQuestions = computed(
  () => questionsData.value?.meta?.total ?? questionsData.value?.length,
)

const isQuestionDrawerVisible = ref(false)
const questionDrawerData = ref()
const questionDeleteData = ref()
const questionDrawerType = ref("Add")

const showQuestion = (questionData, type) => {
  questionDrawerData.value = questionData
  questionDrawerType.value = type
  isQuestionDrawerVisible.value = true
}

const deleteQuestion = async (confirm, data) => {
  if (data?.id && confirm) {
    await questionService.deleteQuestion(
      data.id,
      () => {
        fetchQuestions()
        isConfirmDialogVisible.value = false
      },
      onError,
    )
  }
}

const showConfirmDialog = questionData => {
  questionDeleteData.value = questionData
  isConfirmDialogVisible.value = true
}

watch(isConfirmDialogVisible, () => {
  if (!isConfirmDialogVisible.value) {
    questionDeleteData.value = {}
  }
})

const getClass = item => {
  return item?.calories > 500
    ? "cal-high"
    : item?.calories > 400
      ? "cal-medium"
      : "cal-low"
}

const refImportQuestionForm = ref()
const importQuestionFile = ref()
const importQuestionErrors = ref()
const isImportQuestionLoading = ref(false)
const isImportQuestionModal = ref(false)

const submitImportQuestion = async () => {
  if (!importQuestionFile.value) {
    importQuestionErrors.value = { file: ["Please select a file to import."] }

    return
  }
  isImportQuestionLoading.value = true
  importQuestionErrors.value = null // Clear previous errors

  try {
    await questionService.import(
      {
        file: importQuestionFile.value,
      },
      () => {
        // onSuccess callback
        snackbar.success("Questions imported successfully.")
        isImportQuestionModal.value = false
        fetchQuestions() // Refresh the question list
        importQuestionFile.value = null // Reset file input
      },
      error => {
        // onError callback
        importQuestionErrors.value = error.data?.errors || {
          message:
            error.data?.message || "File upload failed. Please try again.",
        }
        snackbar.error(error.data?.message || "File upload failed.")
      },
    )
  } catch (e) {
    console.error("Error during question import submission:", e)
    snackbar.error(
      "An unexpected error occurred while submitting the import request.",
    )
    if (!importQuestionErrors.value)
      importQuestionErrors.value = { message: "Import submission failed." }
  } finally {
    isImportQuestionLoading.value = false
  }
}

const downloadSampleQuestionFile = async () => {
  await downloadFile(
    "masters/questions/sample-import-file/export",
    "question-sample-excel.xlsx",
    () => {}, // success
    () => {}, // error
  )
}

watch(
  () => isImportQuestionModal.value,
  value => {
    if (value === false) {
      // When modal is closed
      importQuestionFile.value = null
      importQuestionErrors.value = null
    }
  },
)

const renderKey = ref(0)


const expandedRows = ref([])

const forceRerender = () => {
  renderKey.value++
}

const serverTable = ref(null)
let sortable = null
const isDragging = ref(false)


const setupSortable = async () => {
  await nextTick()

  const tbody = serverTable.value.$el.querySelector('tbody')
  if (!tbody) return
  sortable?.destroy()
  sortable = Sortable.create(tbody, {
    handle: '.drag-handle',
    animation: 150,
    ghostClass: 'sortable-ghost',

    onStart() {
      isDragging.value = true
    },
    onEnd(evt) {
      const sortedOrders = questions.value
        .map(q => q.order)
        .sort((a, b) => a - b)

      const moved = questions.value.splice(evt.oldIndex, 1)[0]

      questions.value.splice(evt.newIndex, 0, moved)

      questions.value.forEach((q, idx) => {
        q.order = sortedOrders[idx]
      })

      const payload = questions.value.map(q => ({
        id: q.id,
        order: q.order,
      }))

      questionService.reOrderQuestion(payload)

      expandedRows.value = []
      
      isDragging.value = false
      forceRerender()
      fetchQuestions()
    },
  })
}

onMounted(setupSortable)

watch(
  [() => page.value, () => itemsPerPage.value, () => expandedRows.value],
  setupSortable,
)

const subTableRefs = ref({})
let subSortables = null

const registerSubTable = (el, parentId) => {
  if (el) subTableRefs.value[parentId] = el
  else delete subTableRefs.value[parentId]
}

const initSubSortables =  () => {
  Object.entries(subTableRefs.value).forEach(([parentId, tableComp]) => {
    const tbody = tableComp.$el.querySelector('tbody')

    // skip if already initialized
    if (!tbody || tbody.dataset.sortableInited) return
    
    subSortables?.destroy()
    subSortables = Sortable.create(tbody, {
      handle: '.sub-drag-handle',
      animation: 150,
      ghostClass: 'sortable-ghost',
      onEnd(evt) {
        const q = questions.value.find(q => q.id === +parentId)
        if (!q?.sub_questions) {
          return
        }

        const sortedOrders = q.sub_questions
          .map(s => s.order)
          .sort((a, b) => a - b)

        const moved = q.sub_questions.splice(evt.oldIndex, 1)[0]

        q.sub_questions.splice(evt.newIndex, 0, moved)

        q.sub_questions.forEach((s, idx) => {
          s.order = sortedOrders[idx]
        })

        const payload = q.sub_questions.map(s => ({
          id: s.id,
          order: s.order,
        }))

        questionService.reOrderQuestion(payload)
        forceRerender()
        nextTick(() => {
          initSubSortables()
        })
      },
    })

    tbody.dataset.sortableInited = 'true'
  })
}

watch(
  () => expandedRows.value,
  async () => {
    await nextTick()
    initSubSortables()
  },
)

onMounted(() => {
  nextTick(initSubSortables)
})

watch(
  [() => selectedFormType.value, () => selectedQuestionGroup.value, () => searchQuery.value],
  ([newFormType, newQuestionGroup, newSearch]) => {
    const route = router.currentRoute.value

    router.replace({
      path: route.path,
      query: {
        ...route.query,
        formType: newFormType || undefined,         
        questionGroup: newQuestionGroup || undefined,
        search: newSearch || undefined,
      },
    })
  },
)
</script>

<template>
  <section>
    <VCard>
      <VCardText class="px-1">
        <VItemGroup
          v-model="selectedFormType"
          selected-class="bg-primary "
          mandatory
          @update:model-value="selectedQuestionGroup = null"
        >
          <VContainer>
            <VRow>
              <VCol
                v-for="formType in formTypes"
                :key="formType.id"
                cols="12"
                md="2"
              >
                <VItem
                  v-slot="{ isSelected, selectedClass, toggle }"
                  :value="formType.id"
                >
                  <VCard
                    class="d-flex align-center cursor-pointer"
                    :class="[selectedClass]"
                    height="50"
                    variant="outlined"
                    dark
                    @click="toggle"
                  >
                    <div
                      class="text-body-2 flex-grow-1 text-center"
                      :class="isSelected ? 'text-white' : ''"
                    >
                      {{ formType.name }}
                    </div>
                  </VCard>
                </VItem>
              </VCol>
            </VRow>
          </VContainer>
        </VItemGroup>
      </VCardText>
    </VCard>
    <VCard class="mt-4">
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
              { value: -1, title: 'All' },
            ]"
            style="inline-size: 6.25rem"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-3">
          <!-- 👉 Search  -->
          <div style="inline-size: 15rem">
            <AppSelect
              v-model="selectedDepartment"
              :loading="isFetchingDepartment"
              :items="departments"
              :item-title="(item) => item.name"
              :item-value="(item) => item.id"
              clearable
              placeholder="Filter Department"
            />
          </div>
          <div style="inline-size: 20rem">
            <AppSelect
              v-model="selectedQuestionGroup"
              :loading="isFetchingQuestionGroup"
              :items="questionGroups"
              :item-title="(item) => item.name"
              :item-value="(item) => item.id"
              clearable
              placeholder="Filter Question Group"
            />
          </div>

          <div style="inline-size: 15rem">
            <AppTextField
              v-model="searchQuery"
              placeholder="Search"
              density="compact"
            />
          </div>

          <!-- 👉 Export button -->
          <!--
            <VBtn
            variant="tonal"
            color="secondary"
            prepend-icon="tabler-screen-share"
            >
            Export
            </VBtn> 
          -->
          <!-- 👉 Import Questions Button -->
          <VBtn
            v-if="$can('import', 'question')"
            prepend-icon="tabler-download"
            @click="isImportQuestionModal = !isImportQuestionModal"
          >
            Import Questions
          </VBtn> 

          <VBtn
            prepend-icon="tabler-plus"
            :to="{ name: 'app-feedback-question-add' }"
            class="font-weight-medium text-link"
          >
            Add New Question
          </VBtn>
        </div>
      </VCardText>
    </VCard>
    <VCard class="mt-4">
      <VDivider />

      <VDataTableServer
        ref="serverTable" 
        :key="renderKey"
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        v-model:expanded="expandedRows"
        :loading="isFetchingQuestions"
        :items="questions ?? []"
        :items-length="totalQuestions ?? 0"
        item-key="id"
        :headers="headers"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #expanded-row="{ item }">
          <tr
            v-if="!isDragging"
            :data-parent-id="`${item.id}`"
            class="!text-normal"
          >
            <!-- dynamically span all columns -->
            <td
              :colspan="headers.length"
              class="level-bg-color px-6 py-6 overflow-hidden"
            >
              <VCard
                variant="outlined w-100"
                class="table-container"
              >
                <VDataTable
                  :ref="el => registerSubTable(el, item.id)"
                  :items="item.sub_questions || []"
                  :headers="subQuestionsHeader"
                  hide-default-footer
                  disable-sort
                  :items-per-page="-1"
                  class="custom-data-table text-no-wrap overflow-auto"
                >
                  <template #item.drag>
                    <VIcon
                      class="sub-drag-handle"
                      icon="tabler-arrows-maximize"
                      size="20"
                    />
                  </template>
                  <template #item.label="{ item: sub }">
                    <VMenu
                      open-on-hover
                      :close-on-content-click="false"
                    >
                      <template #activator="{ props }">
                        <span v-bind="props">
                          {{
                            sub.label?.substring(0, 25) +
                              (sub.label?.length > 25 ? "..." : "") ||
                              "NA"
                          }}
                        </span>
                      </template>
                      <VCard>
                        <VList>
                          <VListItem class="mx-0">
                            <template #title>
                              <VListItemTitle class="text-h6 text-primary text-capitalize">
                                {{ sub.label || "NA" }}
                              </VListItemTitle>
                            </template>
                          </VListItem>
                        </VList>
                      </VCard>
                    </VMenu>
                  </template>

                  <template #item.name="{ item: sub }">
                    <div
                      class="d-flex align-center cursor-pointer"
                      @click="showQuestion(sub, 'View')"
                    >
                      <VAvatar
                        size="34"
                        :variant="!sub.avatar ? 'tonal' : undefined"
                        class="me-3"
                      >
                        <VImg
                          v-if="sub.avatar"
                          :src="sub.avatar"
                        />
                        <span v-else>{{ avatarText(sub.name) }}</span>
                      </VAvatar>
                      <div class="d-flex flex-column">
                        <h6 class="text-base">
                          {{ sub.name }}
                        </h6>
                      </div>
                    </div>
                  </template>

                  <template #item.total_questions="{ item: sub }">
                    <span class="text-capitalize font-weight-medium">
                      {{ sub.questions?.length }}
                    </span>
                  </template>

                  <template #item.status="{ item: sub }">
                    <span class="text-capitalize font-weight-medium">
                      {{ sub.status ? "Active" : "Inactive" }}
                    </span>
                  </template>

                  <template #item.actions="{ item: sub }">
                    <IconBtn
                      v-if="$can('delete','master.question')"
                      @click="showConfirmDialog(sub)"
                    >
                      <VIcon icon="tabler-trash" />
                    </IconBtn>
                    <RouterLink
                      v-if="$can('update','master.question')"
                      :to="{ name:'app-feedback-question-tab', params:{ id: sub.id } }"
                      class="font-weight-medium text-link"
                    >
                      <VIcon icon="tabler-edit" />
                    </RouterLink>
                  </template>
                </VDataTable>
              </VCard>
            </td>
          </tr>
        </template>

        <template #item.label="{ item }">
          <VMenu
            open-on-hover
            :close-on-content-click="false"
          >
            <template #activator="{ props }">
              <span v-bind="props">
                {{
                  item.label?.substring(0, 25) +
                    (item.label?.length > 25 ? "..." : "") ?? "NA"
                }}
              </span>
            </template>
            <VCard>
              <VList>
                <VListItem class="mx-0">
                  <template #title>
                    <VListItemTitle class="text-h6 text-primary text-capitalize">
                      {{ item.label ?? "NA" }}
                    </VListItemTitle>
                  </template>
                </VListItem>
              </VList>
            </VCard>
          </VMenu>
        </template>
        <template #item.drag>
          <VIcon
            class="drag-handle"
            icon="tabler-arrows-maximize"
            size="20"
          />
        </template>

        <template #item.name="{ item }">
          <div
            class="d-flex align-center cursor-pointer"
            :questionData="item"
            @click="showQuestion(item, 'View')"
          >
            <VAvatar
              size="34"
              :variant="!item.avatar ? 'tonal' : undefined"
              class="me-3"
            >
              <VImg
                v-if="item.avatar"
                :src="item.avatar"
              />
              <span v-else>{{ avatarText(item.name) }}</span>
            </VAvatar>
            <div class="d-flex flex-column">
              <h6 class="text-base">
                {{ item.name }}
              </h6>
            </div>
          </div>
        </template>
        <template #item.department="{ item }">
          <span class="text-capitalize font-weight-medium">{{
            item.department?.name
          }}</span>
        </template>
        <template #item.total_questions="{ item }">
          <span class="text-capitalize font-weight-medium">{{
            item.questions?.length
          }}</span>
        </template>
        <template #item.status="{ item }">
          <span class="text-capitalize font-weight-medium">{{
            item.status ? "Active" : "Inactive"
          }}</span>
        </template>

        <template #item.actions="{ item }">
          <IconBtn
            v-if="$can('delete', 'master.question')"
            @click="showConfirmDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <RouterLink
            v-if="$can('update', 'master.question')"
            :to="{ name: 'app-feedback-question-tab', params: { id: item.id } }"
            class="font-weight-medium text-link"
          >
            <VIcon icon="tabler-edit" />
          </RouterLink>
        </template>
      </VDataTableServer>
    </VCard>
    <!--
      <QuestionDrawer
      v-model:isDrawerOpen="isQuestionDrawerVisible"
      :question-group-data="questionDrawerData"
      :type="questionDrawerType ?? 'Add'"
      :errors="errors"
      @update:type="questionDrawerType = $event"
      @update:question-group-data="updateQuestion"
      @save-question-group="saveQuestion"
      /> 
    -->
    <ConfirmDialog
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Question Deleted."
      confirm-title="Deleted!"
      :data="questionDeleteData"
      @confirm="deleteQuestion"
    />
    <!-- Import Question Modal -->
    <VDialog
      v-model="isImportQuestionModal"
      max-width="600"
      :persistent="isImportQuestionLoading"
    >
      <DialogCloseBtn
        size="small"
        :disabled="isImportQuestionLoading"
        @click="isImportQuestionModal = false"
      />
      <VCard>
        <VCardText class="pt-2">
          <h6 class="text-lg font-weight-medium mt-5">
            Import Questions
          </h6>
        </VCardText>

        <VCardText>
          <VForm ref="refImportQuestionForm">
            <VFileInput
              label="Upload File"
              prepend-icon="tabler-file-import"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              :disabled="isImportQuestionLoading"
              :error-messages="importQuestionErrors?.file"
              @change="
                (e) => {
                  importQuestionFile = e.target.files[0];
                }
              "
            />
          </VForm>
        </VCardText>

        <!-- General Errors -->
        <VCardText
          v-if="
            importQuestionErrors?.message &&
              !Array.isArray(importQuestionErrors)
          "
        >
          <VAlert
            color="error"
            variant="tonal"
            closable
            @click:close="importQuestionErrors.message = null"
          >
            {{ importQuestionErrors.message }}
          </VAlert>
        </VCardText>

        <!-- Validation Errors (row-specific from backend) -->
        <VCardText
          v-if="
            Array.isArray(importQuestionErrors) &&
              importQuestionErrors.length > 0
          "
        >
          <div
            class="d-flex flex-column gap-2"
            style="max-block-size: 200px; overflow-y: auto"
          >
            <VAlert
              v-for="(error, index) in importQuestionErrors"
              :key="index"
              color="error"
              variant="tonal"
              density="compact"
            >
              {{ error.row ? `Row ${error.row}: ` : ""
              }}{{ error.message || error }}
            </VAlert>
          </div>
        </VCardText>

        <!-- Loading Bar -->
        <VCardText
          v-if="isImportQuestionLoading"
          class="pt-0 pb-2"
        >
          <VProgressLinear
            indeterminate
            color="primary"
            height="6"
            rounded
            class="mb-1"
          />
        </VCardText>

        <!-- Action Buttons -->
        <VCardActions class="px-5 pb-5">
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="isImportQuestionLoading"
            @click="downloadSampleQuestionFile"
          >
            Download Sample
          </VBtn>
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="isImportQuestionLoading"
            @click="isImportQuestionModal = false"
          >
            Close
          </VBtn>
          <VBtn
            :loading="isImportQuestionLoading"
            :disabled="isImportQuestionLoading || !importQuestionFile"
            @click="submitImportQuestion"
          >
            Import
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>

<style>
.level-bg-color {
  background-color: #f7f7f7;
}

.non-selectable {
  user-select: none !important;
}

/* stylelint-disable-next-line no-descending-specificity */
.custom-data-table .v-data-table-footer {
  display: none !important;
}
</style>
